import { ExcelFormatter } from "./excel-formatter";
import { ExcelFormulaBuilder } from "./excel-formula-builder";
import { STATUS_NREL, TABLE_CONFIG } from "./excel-constants";
import { toNumber } from "./rational";

/**
 * Handles writing data to Excel worksheets
 */
export class ExcelWriter {
  constructor(workbook) {
    this.workbook = workbook;
    this.workSheet = null;
    this.formatter = null;
    this.formulaBuilder = new ExcelFormulaBuilder();
  }

  /**
   * Generates a complete worksheet with all data and formatting
   */
  async generateWorksheet(props) {
    this.workSheet = this.workbook.addWorksheet("Tasks");
    this.formatter = new ExcelFormatter(this.workSheet);

    const includeFormulas = props.includeFormulas !== false;
    const includeChildren = props.includeChildren !== false;

    const tableRows = this.buildTableRows(props, { includeChildren });

    this.addSummaryRow(props);
    this.addTable(props, tableRows);
    this.applyFormatting(props, tableRows, { includeChildren });
    if (includeFormulas) {
      this.applyFormulas(props, tableRows, { includeChildren });
    }
    this.addDropdowns(props, tableRows, { includeChildren });
  }

  /**
   * Builds the data rows for the table
   */
  buildTableRows(props, options = { includeChildren: true }) {
    const rows = [];
    const includeChildren = options.includeChildren !== false;

    const normalizeFulfillment = (value) => {
      if (value === "n.rel.") return value;
      const num = toNumber(value);
      if (!Number.isFinite(num)) return "";
      return num > 1 ? num / 100 : num;
    };
    const normalizeStepValue = (value) => {
      if (typeof value === "number") {
        return value > 1 ? value / 100 : value;
      }
      return value;
    };

    props.deliverables.forEach((item) => {
      if (item.isChild) {
        // Parent row
        const responsibleValues = props.responsibles.map((r) => item[r.identity]);
        rows.push([
          item.id,
          item.description,
          toNumber(item.weight),
          ...responsibleValues,
          ...props.steps.map((s) => normalizeStepValue(item[s.identity])),
          normalizeFulfillment(item.fulfillment),
        ]);

        // Child rows
        if (includeChildren) {
          item.children.forEach((child) => {
            const childRow = [
              child.id,
              child.description,
              toNumber(child.weight),
              ...props.responsibles.map((r) => child[r.identity]),
              ...props.steps.map((s) => child[s.identity]),
              normalizeFulfillment(child.fulfillment),
            ];
            rows.push(childRow);
          });
        }
      } else {
        // Regular row
        const row = [
          item.id,
          item.description,
          toNumber(item.weight),
          ...props.responsibles.map((r) => item[r.identity]),
          ...props.steps.map((s) => item[s.identity]),
          normalizeFulfillment(item.fulfillment),
        ];
        rows.push(row);
      }
    });

    return rows;
  }

  /**
   * Adds the summary row with step fulfillments
   */
  addSummaryRow(props) {
    const emptyResponsibles = new Array(props.responsibles.length).fill("");
    const stepValues = props.steps.map((step) => {
      const raw = props.stepsFulfillment?.[step.identity];
      if (raw === STATUS_NREL || raw === undefined || raw === null) return STATUS_NREL;
      const num = Number(raw);
      if (!Number.isFinite(num)) return STATUS_NREL;
      return num > 1 ? num / 100 : num;
    });

    const normalizeTotalFulfillment = () => {
      if (props.totalFulfillment === STATUS_NREL) return STATUS_NREL;
      const num = Number(props.totalFulfillment);
      if (!Number.isFinite(num)) return STATUS_NREL;
      return num > 1 ? num / 100 : num;
    };

    const summaryRow = ["", "", "", ...emptyResponsibles, ...stepValues, normalizeTotalFulfillment()];

    this.workSheet.addRow(summaryRow);
  }

  /**
   * Creates the Excel table
   */
  addTable(props, tableRows) {
    const headers = props.headers.map((header) => ({
      name: header.text,
      filterButton: true,
    }));

    this.workSheet.addTable({
      name: TABLE_CONFIG.name,
      ref: `A${TABLE_CONFIG.startRow}`,
      headerRow: true,
      style: { showRowStripes: TABLE_CONFIG.showRowStripes },
      columns: headers,
      rows: tableRows,
    });
  }

  /**
   * Applies all formatting to the worksheet
   */
  applyFormatting(props, tableRows, options = { includeChildren: true }) {
    // Format summary row
    this.formatter.formatSummaryRow(0, props.steps.length, props);

    // Format fulfillment column
    const fulfillmentColIndex = 3 + props.responsibles.length + props.steps.length;
    const fulfillmentColLetter = this.workSheet._columns[fulfillmentColIndex].letter;
    this.formatter.formatFulfillmentColumn(fulfillmentColLetter, tableRows.length);

    // Format child rows
    if (options.includeChildren !== false) {
      this.formatChildRows(props, tableRows);
    } else {
      this.formatFlatStatusColumns(props, tableRows.length);
    }
  }

  /**
   * Formats child row cells with data bars
   */
  formatChildRows(props, tableRows) {
    const offset = 3 + props.responsibles.length;
    let rowNum = TABLE_CONFIG.dataStartRow;

    props.deliverables.forEach((deliverable) => {
      if (deliverable?.isChild) {
        for (let col = offset; col < offset + props.steps.length; col++) {
          const colLetter = this.workSheet._columns[col].letter;
          const cell = `${colLetter}${rowNum}`;
          this.formatter.addDataBarFormatting(cell);
          this.formatter.addNumberFormatting(cell);
        }
        rowNum += deliverable.children.length + 1;
      } else {
        rowNum++;
      }
    });
  }

  /**
   * Applies all formulas to the worksheet
   */
  applyFormulas(props, tableRows, options = { includeChildren: true }) {
    const parentRows = options.includeChildren === false ? [] : this.getParentRowNumbers(props);
    this.addSummaryFormulas(props, tableRows, parentRows);
    if (options.includeChildren !== false) {
      this.addChildRowFormulas(props);
      this.addFulfillmentFormulas(props, parentRows);
    } else {
      this.addFulfillmentFormulasFlat(props, tableRows.length);
    }
    this.addTotalFulfillmentFormula(props, tableRows, parentRows);
  }

  /**
   * Adds formulas to the summary row
   */
  addSummaryFormulas(props, tableRows, parentRows) {
    const offset = 3 + props.responsibles.length;
    const weightCol = this.workSheet._columns[2].letter;
    const firstDataRow = TABLE_CONFIG.dataStartRow;
    const lastDataRow = firstDataRow + tableRows.length - 1;

    for (let i = offset; i < offset + props.steps.length; i++) {
      const statusCol = this.workSheet._columns[i].letter;

      const formula = this.formulaBuilder.buildSummaryFormula(
        statusCol,
        weightCol,
        firstDataRow,
        lastDataRow,
        parentRows
      );

      this.workSheet.getCell(`${statusCol}1`).value = { formula };
    }
  }

  /**
   * Gets row numbers that are parent rows (have children)
   */
  getParentRowNumbers(props) {
    const parentRows = [];
    let rowNum = TABLE_CONFIG.dataStartRow;

    props.deliverables.forEach((deliverable) => {
      if (deliverable?.isChild) {
        parentRows.push(rowNum);
        rowNum += deliverable.children.length + 1;
      } else {
        rowNum++;
      }
    });

    return parentRows;
  }

  /**
   * Adds formulas to child rows (parent aggregation)
   */
  addChildRowFormulas(props) {
    const offset = 3 + props.responsibles.length;
    const weightCol = this.workSheet._columns[2].letter;
    let rowNum = TABLE_CONFIG.dataStartRow;

    props.deliverables.forEach((deliverable) => {
      if (deliverable?.isChild) {
        const childCount = deliverable.children.length;
        const startRow = rowNum + 1;
        const endRow = rowNum + childCount;

        for (let col = offset; col < offset + props.steps.length; col++) {
          const statusCol = this.workSheet._columns[col].letter;
          const formula = this.formulaBuilder.buildChildRowFormula(statusCol, weightCol, startRow, endRow);
          this.workSheet.getCell(`${statusCol}${rowNum}`).value = { formula };
        }

        rowNum += childCount + 1;
      } else {
        rowNum++;
      }
    });
  }

  /**
   * Adds fulfillment formulas to all rows
   */
  addFulfillmentFormulas(props, parentRows) {
    const fulfillmentColIndex = 3 + props.responsibles.length + props.steps.length;
    const fulfillmentCol = this.workSheet._columns[fulfillmentColIndex].letter;
    const offset = 3 + props.responsibles.length;
    const firstStepCol = this.workSheet._columns[offset].letter;
    const lastStepCol = this.workSheet._columns[offset + props.steps.length - 1].letter;

    let rowNum = TABLE_CONFIG.dataStartRow;

    props.deliverables.forEach((deliverable) => {
      if (deliverable?.isChild) {
        // Parent row gets a placeholder (will show child aggregation from step columns)
        rowNum++;

        // Add formulas to child rows
        deliverable.children.forEach(() => {
          const formula = this.formulaBuilder.buildFulfillmentFormula(firstStepCol, lastStepCol, rowNum);
          this.workSheet.getCell(`${fulfillmentCol}${rowNum}`).value = { formula };
          rowNum++;
        });
      } else {
        const formula = this.formulaBuilder.buildFulfillmentFormula(firstStepCol, lastStepCol, rowNum);
        this.workSheet.getCell(`${fulfillmentCol}${rowNum}`).value = { formula };
        rowNum++;
      }
    });
  }

  /**
   * Adds fulfillment formulas when children are not included (flat export)
   */
  addFulfillmentFormulasFlat(props, rowCount) {
    const fulfillmentColIndex = 3 + props.responsibles.length + props.steps.length;
    const fulfillmentCol = this.workSheet._columns[fulfillmentColIndex].letter;
    const offset = 3 + props.responsibles.length;
    const firstStepCol = this.workSheet._columns[offset].letter;
    const lastStepCol = this.workSheet._columns[offset + props.steps.length - 1].letter;

    let rowNum = TABLE_CONFIG.dataStartRow;
    for (let i = 0; i < rowCount; i++) {
      const formula = this.formulaBuilder.buildFulfillmentFormula(firstStepCol, lastStepCol, rowNum);
      this.workSheet.getCell(`${fulfillmentCol}${rowNum}`).value = { formula };
      rowNum++;
    }
  }

  /**
   * Apply percentage formatting and data bars to status columns when exporting flat (no children)
   */
  formatFlatStatusColumns(props, rowCount) {
    const offset = 3 + props.responsibles.length;
    const startRow = TABLE_CONFIG.dataStartRow;
    const endRow = startRow + rowCount - 1;

    for (let i = offset; i < offset + props.steps.length; i++) {
      const colLetter = this.workSheet._columns[i].letter;
      const range = `${colLetter}${startRow}:${colLetter}${endRow}`;
      this.formatter.addDataBarFormatting(range);
      for (let r = startRow; r <= endRow; r++) {
        this.formatter.addNumberFormatting(`${colLetter}${r}`);
      }
    }
  }

  /**
   * Adds total fulfillment formula to summary row
   */
  addTotalFulfillmentFormula(props, tableRows, parentRows) {
    const fulfillmentColIndex = 3 + props.responsibles.length + props.steps.length;
    const fulfillmentCol = this.workSheet._columns[fulfillmentColIndex].letter;
    const weightCol = this.workSheet._columns[2].letter;
    const firstDataRow = TABLE_CONFIG.dataStartRow;
    const lastDataRow = firstDataRow + tableRows.length - 1;

    const formula = this.formulaBuilder.buildTotalFulfillmentFormula(
      fulfillmentCol,
      weightCol,
      firstDataRow,
      lastDataRow,
      parentRows
    );

    this.workSheet.getCell(`${fulfillmentCol}1`).value = { formula };

    // Format the total fulfillment cell
    this.formatter.addNumberFormatting(`${fulfillmentCol}1`);
    this.formatter.addDataBarFormatting(`${fulfillmentCol}1`);
  }

  /**
   * Adds dropdown validations to status columns
   */
  addDropdowns(props, tableRows, options = { includeChildren: true }) {
    const offset = 3 + props.responsibles.length;
    const startRow = TABLE_CONFIG.dataStartRow;
    const includeChildren = options.includeChildren !== false;

    for (let col = offset; col < offset + props.steps.length; col++) {
      const colLetter = this.workSheet._columns[col].letter;

      if (includeChildren) {
        let currentRow = startRow;
        props.deliverables.forEach((deliverable) => {
          if (deliverable?.isChild) {
            currentRow++; // skip parent row
            deliverable.children.forEach(() => {
              this.formatter.addStatusDropdown(`${colLetter}${currentRow}`);
              currentRow++;
            });
          } else {
            this.formatter.addStatusDropdown(`${colLetter}${currentRow}`);
            currentRow++;
          }
        });
      } else {
        for (let i = 0; i < tableRows.length; i++) {
          const row = startRow + i;
          this.formatter.addStatusDropdown(`${colLetter}${row}`);
        }
      }
    }
  }
}
